import * as _ from 'underscore';
import { DataConnectionTypes } from '../../content/resources/DataConnectionTypes';
import { Favorites } from '../../content/resources/Favorites';
import { ProjectNames } from '../../content/resources/ProjectNames';
import { Tags } from '../../content/resources/Tags';
import { UserNames } from '../../content/resources/UserNames';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { Groups } from '../../userManagement/groups/Groups';
import { Users } from '../../userManagement/services/resources/Users';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { Filter } from '../helpers/Filter';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { GroupKeys, IGroup } from '../ServerApi/types/IGroup';
import { IDataConnectionType } from '../ServerApi/types/IDataConnection';
import { IFavorite } from '../ServerApi/types/IFavorite';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IProjectName } from '../ServerApi/types/IProject';
import { ISiteNameSansId } from '../ServerApi/types/ISiteNameSansId';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { ISliceable } from '../models/ISliceable';
import { ITag } from '../ServerApi/types/ITag';
import { ITypeaheadSource } from '../models/ITypeaheadSource';
import { IUserName, UserKeys } from '../ServerApi/types/IUser';
import { Module } from '../Module';
import { NamedItemKeys } from '../ServerApi/types/resourceParts';
import { Order } from '../helpers/Order';
import { ResourceQuery } from '../resources/ResourceQuery';
import { ScheduleContentType } from '../models/ContentTypes';
import { Schedules } from '../resources/Schedules';
import { SiteNameSansIds } from '../resources/SiteNameSansIds';
import { SiteRolesResource } from '../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../../userManagement/services/resources/SiteUsers';
import { SliceableHelper } from '../helpers/SliceableHelper';


export class SearchSuggestionsService {

  public static $inject = [
    '$q',
    'SiteNamesAcrossAllPods',
    'Schedules',
    'scheduleFrequencyDescriptionFilter'
  ];

  constructor(
    private $q: ng.IQService,
    private SiteNamesAcrossAllPods: SiteNameSansIds,
    private Schedules: Schedules,
    private ScheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter
  ) { }

  public project(id: string): ng.IPromise<IFieldValue> {
    return ProjectNames.getById(id).then((projectName: IProjectName) => {
      return {
        value: projectName ? projectName.id : id,
        label: projectName ? projectName.name : '',
        count: 0
      };
    });
  }

  public sites(matchText: string): ISliceable<ISiteNameSansId> {
    return this.SiteNamesAcrossAllPods.all().withFilters([Filter.search(matchText)]);
  }

  public favorites(matchText: string): ISliceable<IFavorite> {
    return Favorites.all().withFilters([Filter.search(matchText)]);
  }

  public users(matchText: string): ResourceQuery<IUserName> {
    return UserNames.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(UserKeys.displayName)]);
  }

  public usersWithoutGuest(matchText: string): ResourceQuery<IUserName> {
    return this.users(matchText).addFilter(Users.instance.getWithoutGuestFilter());
  }

  public usersCanOwnProject(matchText: string): ResourceQuery<IUserName> {
    return this.users(matchText).addFilter(Users.instance.getCanOwnProjectFilter());
  }

  public user(id: string): ng.IPromise<IFieldValue> {
    return UserNames.getById(id).then((userName: IUserName) => {
      return {
        value: userName ? userName.id : id,
        label: userName ? userName.displayName : '',
        count: 0
      };
    });
  }

  public groups(matchText: string): ISliceable<IGroup> {
    return Groups.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(GroupKeys.name)]);
  }

  public tags(matchText: string): ISliceable<ITag> {
    return Tags.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(NamedItemKeys.name)]);
  }

  public dataConnectionType(dataConnectionType: string): ng.IPromise<IFieldValue> {
    return DataConnectionTypes.getByDataConnectionType(dataConnectionType).then((connectionType: IDataConnectionType) => {
      return {
        value: connectionType ? connectionType.connectionType : dataConnectionType,
        label: connectionType ? connectionType.connectionTypeDisplayName : '',
        count: 0
      };
    });
  }

  public schedulesSource(scheduledActionType?: string): ITypeaheadSource {
    const searchFilterFn = (schedule: IScheduleInfo, search: string) => {
      if(!search) return true;

      const scheduleName = getSessionManagementServiceInstance().displayScheduleDescriptionAsName() ? this.ScheduleFrequencyDescriptionFilter(schedule) : schedule.name;
      return _.all(search.toLowerCase().split(' '), (searchPart) => (scheduleName.toLowerCase().indexOf(searchPart) >= 0));
    };

    let scheduleResourceQuery = this.Schedules.all().withOrder(ScheduleContentType.defaultOrder);
    if(scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(Schedules.filterByActionType(scheduledActionType));
    }

    const scheduleListPromise = SliceableHelper.fetchAll(scheduleResourceQuery).then((schedulesList) => schedulesList.items);

    return SliceableHelper.searchArray(searchFilterFn, scheduleListPromise, this.$q);
  }

  public schedulesQuery(scheduledActionType?: string): ResourceQuery<IScheduleInfo> {
    let scheduleResourceQuery = this.Schedules.all().withOrder(ScheduleContentType.defaultOrder);
    if(scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(Schedules.filterByActionType(scheduledActionType));
    }
    return scheduleResourceQuery;
  }

  // gets users with siteRole filter and search string
  public usersBySiteRoleAndSearch(siteRole: string, searchText: string, resultOrder: IOrderClause<string>): ResourceQuery<ISiteUserInfo> {
    if (siteRole === SiteRolesResource.AnyRole) {
      return SiteUsers.instance.all().withOrder([resultOrder])
      .addFilter(Filter.search(searchText));
    }
    return SiteUsers.instance.all().withOrder([resultOrder])
    .addFilter(Filter.stringFieldEq(FilterFieldNames.siteRole, siteRole))
    .addFilter(Filter.search(searchText));
  }
}

Module.instance().service('SearchSuggestionsService', SearchSuggestionsService);
